// frontend/src/pages/Dashboard.jsx
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import { api } from '../utils/api';
import { useLanguage } from '../utils/i18n';
import AddClientModal from '../components/AddClientModal';
import QRScanner from '../components/QRScanner';
import BusinessQRModal from '../components/BusinessQRModal';
import ClientCard from '../components/ClientCard';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { useNotification } from '../utils/NotificationContext';
import InstallBanner from '../components/InstallBanner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Maximize,
  QrCode,
  UserPlus,
  Users,
  Zap,
  Gift,
  TrendingUp,
  LayoutDashboard,
  Palette,
  BarChart3,
  Settings,
  ChevronRight,
  User,
  Search,
  LogOut,
  CreditCard,
  Mail,
  Building,
  Info,
  Send,
  Bell,
  Download,
  Sparkles
} from 'lucide-react';

// ─── Cooldown Timer (inline helper) ─────────────────────────
function CooldownTimer({ nextAvailable, onExpired }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!nextAvailable) return;
    const tick = () => {
      const diff = new Date(nextAvailable).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('');
        onExpired?.();
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${h}h ${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [nextAvailable, onExpired]);

  return <strong style={{ color: 'var(--text-muted)' }}>{timeLeft}</strong>;
}

export default function Dashboard() {
  const { business, session, signOut, subscriptionStatus, subscriptionEndDate, planType, needsMigration, isDemo } = useAuth();
  const { t, lang } = useLanguage();
  const { showNotification } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview'); // 'overview', 'clients', 'settings'
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showBizQR, setShowBizQR] = useState(false);
  const [showConversionInfo, setShowConversionInfo] = useState(false);

  // Notification state
  const [notifMessage, setNotifMessage] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [notifStatus, setNotifStatus] = useState({ canSend: true, clientCount: 0, lastSent: null });

  // Sync activeTab with location state
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state?.tab]);

  // Fetch notification status
  useEffect(() => {
    if (!business?.id) return;
    api.getNotificationStatus(business.id)
      .then(setNotifStatus)
      .catch(() => { }); // silent fail
  }, [business?.id]);

  // React Query Fetching (with automatic background polling every 30s)
  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ['clients', business?.id],
    queryFn: () => api.getClients(business.id),
    enabled: !!business?.id,
    refetchInterval: 30000
  });

  const { data: activity = [] } = useQuery({
    queryKey: ['activity', business?.id],
    queryFn: () => api.getActivity(business.id),
    enabled: !!business?.id,
    refetchInterval: 30000
  });

  const { data: stats = { clientTrend: 0, visitsTrend: 0, conversionRate: 0, conversionTrend: 0 } } = useQuery({
    queryKey: ['stats', business?.id],
    queryFn: () => api.getDashboardStats(business.id),
    enabled: !!business?.id,
    refetchInterval: 30000
  });

  // A helper to quickly force a refresh instantly without waiting for the 30s interval
  // We call this right after scanning a QR or adding a client
  const refreshDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['clients', business?.id] });
    queryClient.invalidateQueries({ queryKey: ['activity', business?.id] });
    queryClient.invalidateQueries({ queryKey: ['stats', business?.id] });
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const onDemoClick = (e) => {
    if (isDemo) {
      e.preventDefault();
      showNotification(t('demo_feature_restricted'), 'info');
    }
  };

  const totalVisits = clients.reduce((sum, c) => sum + c.currentCount, 0);
  const rewardsDue = clients.filter(c => c.currentCount >= c.threshold).length;

  const handleDownloadCSV = () => {
    if (clients.length === 0) {
      showNotification(t('no_clients_to_export') || 'No hay clientes para exportar', 'info');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Visits', 'Threshold', 'Last Visit', 'Joined'];
    const rows = clients.map(c => [
      `"${c.name.replace(/"/g, '""')}"`,
      `"${(c.email || '').replace(/"/g, '""')}"`,
      `"${(c.phone || '').replace(/"/g, '""')}"`,
      c.currentCount,
      c.threshold,
      c.lastVisit ? new Date(c.lastVisit).toLocaleDateString() : '-',
      new Date(c.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification(t('csv_export_success') || 'CSV exportado correctamente', 'success');
  };

  if (!business) return (
    <div className="page container" style={{ textAlign: 'center', paddingTop: 80 }}>
      <p>{t('loading')}</p>
    </div>
  );

  const formatTime = (timestamp) => {
    const diff = new Date() - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return t('now') || 'Ahora';
  };

  const MetricCard = ({ icon: Icon, trend, value, label, tooltipText }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div className="metric-card-compact">
        <div className="metric-header">
          <Icon size={16} />
          {trend !== 0 && trend !== undefined && (
            <div className={`trend-indicator ${trend > 0 ? 'trend-up' : 'trend-down'}`}>
              <TrendingUp size={12} style={{ transform: trend < 0 ? 'scaleY(-1)' : 'none' }} />
              <span>{trend > 0 ? '+' : ''}{trend}%</span>
            </div>
          )}
        </div>
        <div className="metric-value-box">
          <span className="metric-main-value">{value}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
            <span className="metric-label-premium">{label}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(!showTooltip);
              }}
              className="info-btn-premium"
              title={tooltipText}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-dim)',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
            >
              <Info size={14} />
            </button>

            {showTooltip && (
              <div
                className="tooltip-premium"
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: '8px',
                  width: '200px',
                  padding: '10px 12px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  fontSize: '11px',
                  lineHeight: '1.4',
                  color: 'var(--text-muted)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  textAlign: 'center',
                  pointerEvents: 'none',
                  animation: 'tooltipAppear 0.2s ease-out'
                }}
              >
                {tooltipText}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  marginLeft: '-6px',
                  borderWidth: '6px',
                  borderStyle: 'solid',
                  borderColor: 'var(--border) transparent transparent transparent'
                }}></div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => (
    <>
      {/* Migration Banner for legacy Paddle users */}
      {needsMigration && (() => {
        const endDate = subscriptionEndDate ? new Date(subscriptionEndDate) : null;
        const now = new Date();
        const daysLeft = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 0;
        const isExpired = endDate && daysLeft <= 0;

        const handleMigrationCheckout = async () => {
          if (!business?.id || !session) return;
          try {
            const productId = planType === 'yearly'
              ? process.env.REACT_APP_POLAR_YEARLY_PRODUCT_ID
              : process.env.REACT_APP_POLAR_MONTHLY_PRODUCT_ID;

            const result = await api.createCheckout(business.id, productId, lang);
            if (result?.url) {
              window.location.href = result.url;
            }
          } catch (err) {
            console.error('[Migration] Checkout error:', err);
            // Fallback to pricing page
            navigate('/pricing');
          }
        };

        const planLabel = planType === 'yearly'
          ? (t('yearly') || 'yearly')
          : (t('monthly') || 'monthly');

        return (
        <section className="dashboard-section-padding" style={{ paddingBottom: 0 }}>
          <div style={{
            background: isExpired
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(239, 68, 68, 0.04))'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.04))',
            border: `1px solid ${isExpired ? 'rgba(239, 68, 68, 0.25)' : 'rgba(99, 102, 241, 0.25)'}`,
            borderRadius: 16,
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
          }}>
            <div style={{
              background: isExpired ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.15)',
              borderRadius: 12,
              padding: 10,
              flexShrink: 0
            }}>
              {isExpired ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                  <path d="M12 9v4"/><path d="M12 17h.01"/>
                </svg>
              ) : (
                <CreditCard size={20} color="#6366f1" />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: 'var(--text)' }}>
                {isExpired
                  ? t('migration_alert_expired_title') || '⚠️ Action required'
                  : t('migration_alert_update_title') || '💳 Update your payment method'
                }
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, margin: '0 0 12px' }}>
                {isExpired
                  ? (t('migration_alert_expired_desc')?.replace('{{plan}}', planLabel) || `Your subscription has ended. Reactivate your ${planLabel} plan to continue managing your loyalty cards and clients.`)
                  : endDate
                    ? (t('migration_alert_update_desc_date')?.replace('{{plan}}', planLabel).replace('{{date}}', endDate.toLocaleDateString()) || `We've switched to a new payment provider. Your ${planLabel} plan is active until ${endDate.toLocaleDateString()} — just add your card now and you won't be charged until then. It only takes a minute!`)
                    : (t('migration_alert_update_desc_nodate')?.replace('{{plan}}', planLabel) || `We've switched to a new payment provider. Please add your payment details to keep your ${planLabel} plan active.`)
                }
              </p>
              <button 
                className="btn-primary-premium"
                style={{ padding: '8px 16px', fontSize: 14, flexShrink: 0, opacity: business?.polar_customer_id === 'pending' ? 0.7 : 1 }}
                onClick={handleMigrationCheckout}
                disabled={business?.polar_customer_id === 'pending'}
              >
                {business?.polar_customer_id === 'pending'
                  ? t('activating') || 'Activating...'
                  : isExpired 
                    ? (t('migration_btn_reactivate')?.replace('{{plan}}', planLabel) || `Reactivate ${planLabel} plan`)
                    : (t('migration_btn_secure')?.replace('{{plan}}', planLabel) || `Secure my ${planLabel} plan`)
                }
              </button>
            </div>
          </div>
        </section>
        );
      })()}

      <section className="greeting-section">
        <h1 className="greeting-title">
          {t('hello_prefix')} {business.name} 👋
        </h1>
      </section>

      {/* ─── Onboarding & Quick Start Checklist for New Users ─── */}
      {!isDemo && (
        <OnboardingChecklist
          business={business}
          userEmail={session?.user?.email}
          clients={clients}
          totalVisits={totalVisits}
          onOpenBizQR={() => setShowBizQR(true)}
          onOpenScanner={() => setShowScanner(true)}
          onRefresh={refreshDashboard}
          isDemo={isDemo}
        />
      )}

      <section className="dashboard-actions">
        <button className="btn-primary-gradient" onClick={() => setShowScanner(true)}>
          <Maximize size={22} />
          <span>{t('scan_qr')}</span>
        </button>

        <div className="secondary-actions-row">
          <button className="btn-outline-premium" onClick={() => setShowBizQR(true)}>
            <QrCode size={18} />
            <span>{t('my_qr_code')}</span>
          </button>
          <button className="btn-outline-premium" onClick={() => setShowAddModal(true)}>
            <UserPlus size={18} />
            <span>{t('add_client')}</span>
          </button>
        </div>
      </section>

      {/* ─── Notification Section ─────────────────────────── */}
      <section className="dashboard-section-padding">
        <div className="glass-card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Bell size={18} color="var(--primary)" />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0A0A0A', margin: 0 }}>{t('notif_title')}</h3>
            </div>
            {notifStatus.remaining !== undefined && (
              <span style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 500 }}>
                {notifStatus.remaining}/{notifStatus.limit || 5} {t('notif_remaining')}
              </span>
            )}
          </div>

          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              type="text"
              value={notifMessage}
              onChange={(e) => setNotifMessage(e.target.value.slice(0, 100))}
              placeholder={t('notif_placeholder')}
              disabled={!notifStatus.canSend || notifSending}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                color: '#0A0A0A',
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <span style={{
              position: 'absolute',
              right: 12,
              bottom: -18,
              fontSize: 11,
              color: notifMessage.length >= 90 ? '#f87171' : 'var(--text-dim)'
            }}>
              {notifMessage.length}/100 {t('notif_char_limit')}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24 }}>
            {notifStatus.canSend ? (
              <button
                className="btn btn-primary btn-sm"
                disabled={notifSending || !notifMessage.trim() || notifStatus.clientCount === 0}
                onClick={async () => {
                  if (!notifMessage.trim()) return;
                  setNotifSending(true);
                  try {
                    await api.sendNotification(business.id, notifMessage.trim());
                    showNotification(t('notif_success'), 'success');
                    setNotifMessage('');
                    // Refresh status
                    const status = await api.getNotificationStatus(business.id);
                    setNotifStatus(status);
                  } catch (err) {
                    showNotification(err.message, 'error');
                  } finally {
                    setNotifSending(false);
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <Send size={14} />
                {notifSending
                  ? t('notif_sending')
                  : `${t('notif_send')} ${notifStatus.clientCount} ${t('notif_clients_suffix')}`}
              </button>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                ⏳ {t('notif_cooldown')}{' '}
                <CooldownTimer nextAvailable={notifStatus.nextAvailable} onExpired={async () => {
                  const status = await api.getNotificationStatus(business.id);
                  setNotifStatus(status);
                }} />
              </span>
            )}

            {notifStatus.lastSent && (
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {t('notif_last_sent')}: {new Date(notifStatus.lastSent).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="dashboard-section-padding" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBlock: '12px' }}>
        <h2 className="activity-title-premium" style={{ margin: 0 }}>{t('analytics_title')}</h2>
        <Link to="/dashboard/analytics" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>
          {t('view_all')}
          <ChevronRight size={16} />
        </Link>
      </div>

      <section className="metrics-grid-premium">
        <MetricCard
          icon={Users}
          trend={stats.clientTrend}
          value={clients.length}
          label={t('clients')}
          tooltipText={t('clients_tooltip')}
        />
        <MetricCard
          icon={Zap}
          trend={stats.visitsTrend}
          value={totalVisits}
          label={t('visits_today')}
          tooltipText={t('visits_tooltip')}
        />
        <MetricCard
          icon={Gift}
          value={rewardsDue}
          label={t('rewards')}
          tooltipText={t('rewards_tooltip')}
        />
        <MetricCard
          icon={BarChart3}
          trend={stats.conversionTrend}
          value={`${stats.conversionRate || 0}%`}
          label={t('conversion')}
          tooltipText={t('conversion_tooltip')}
        />
      </section>

      <section className="activity-section-premium">
        <div className="activity-header-premium">
          <h2 className="activity-title-premium">{t('recent_activity')}</h2>
        </div>
        <div className="activity-list-premium">
          {activity.length > 0 ? (
            activity.map((item) => (
              <div key={item.id} className="activity-item-premium">
                <div className="activity-avatar">
                  {item.type === 'visit' ? <Zap size={16} /> : <User size={18} />}
                </div>
                <div className="activity-info">
                  <span className="activity-name-premium">{item.clientName}</span>
                  <span className="activity-desc-premium">
                    {item.type === 'visit' ? t('activity_visit') : t('activity_reg')}
                  </span>
                </div>
                <div className="activity-time-premium">{formatTime(item.timestamp)}</div>
              </div>
            ))
          ) : (
            <p style={{ padding: '20px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
              {t('no_recent_activity')}
            </p>
          )}
        </div>
      </section>
    </>
  );

  const renderClients = () => (
    <div>
      <section className="greeting-section">
        <h1 className="greeting-title">{t('clients')}</h1>
      </section>

      <div className="search-container-premium" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search className="search-icon-premium" size={20} />
          <input
            type="text"
            className="search-input-premium"
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <button
          className="btn btn-outline"
          onClick={handleDownloadCSV}
          title={t('download_csv') || 'Descargar CSV'}
          style={{ padding: '12px', borderRadius: '12px', height: '48px', width: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Download size={20} />
        </button>
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {filteredClients.map(client => (
          <ClientCard key={client.id} client={client} onRefresh={() => refreshDashboard(false)} />
        ))}
        {filteredClients.length === 0 && (
          <div style={{ textAlign: 'center', py: 40, color: 'var(--text-dim)' }}>
            <Users size={48} style={{ opacity: 0.1, marginBottom: 16 }} />
            <p>{t('no_clients_found')}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => {
    return (
      <div>
        <section className="greeting-section">
          <h1 className="greeting-title">{t('settings')}</h1>
        </section>

        <div className="settings-header-premium">{t('business_account')}</div>
        <div className="settings-group-premium">
          <div className="settings-item-premium">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Building size={18} className="text-dim" />
              <span className="settings-label-premium">{t('business_name')}</span>
            </div>
            <span className="settings-value-premium">{business.name}</span>
          </div>
          <div className="settings-item-premium">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Mail size={18} className="text-dim" />
              <span className="settings-label-premium">{t('user_email')}</span>
            </div>
            <span className="settings-value-premium">{session?.user?.email}</span>
          </div>
          {!isDemo && (
            <div
              className="settings-item-premium"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                localStorage.removeItem(`wafiyo_onboarding_dismissed_${business?.id || 'demo'}`);
                showNotification(t('onboarding_reopened') || 'Quick setup guide reopened on your dashboard!', 'success');
                setActiveTab('overview');
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Sparkles size={18} className="text-dim" color="var(--primary)" />
                <span className="settings-label-premium">{t('onboarding_reopen_btn') || 'Quick Setup Guide'}</span>
              </div>
              <ChevronRight size={18} className="text-dim" />
            </div>
          )}
        </div>

        {session?.user?.email === 'wafiyo.co@gmail.com' && (
          <>
            <div className="settings-header-premium">Admin Control</div>
            <div className="settings-group-premium">
              <div className="settings-item-premium" style={{ cursor: 'pointer' }} onClick={() => navigate('/admin')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <TrendingUp size={18} className="text-dim" />
                  <span className="settings-label-premium">Supervise Wafiyo</span>
                </div>
                <ChevronRight size={18} className="text-dim" />
              </div>
            </div>
          </>
        )}

        <div className="settings-header-premium">{t('subscription')}</div>
        <div className="settings-group-premium">
          <div className="settings-item-premium">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CreditCard size={18} className="text-dim" />
              <span className="settings-label-premium">{t('plan_status')}</span>
            </div>
            <span className={`badge-status-premium ${subscriptionStatus === 'active' || subscriptionStatus === 'trialing' ? 'badge-status-active' : 'badge-status-inactive'}`}>
              {subscriptionStatus === 'active' || subscriptionStatus === 'trialing' ? t('active') : t('inactive')}
            </span>
          </div>
          {(subscriptionStatus === 'active' || subscriptionStatus === 'trialing') && (
            <div className="settings-item-premium" 
              style={{ cursor: business?.polar_customer_id === 'pending' ? 'default' : 'pointer', opacity: business?.polar_customer_id === 'pending' ? 0.7 : 1 }} 
              onClick={isDemo ? onDemoClick : async (e) => {
                if (business?.polar_customer_id === 'pending') return;
                
                // Show visual feedback
                const target = e.currentTarget;
                const originalOpacity = target.style.opacity;
                target.style.opacity = '0.5';
                
                try {
                  const { url } = await api.getPortalSession(business.id);
                  if (url) {
                    if (url.startsWith('/')) {
                      navigate(url);
                    } else {
                      window.location.href = url;
                    }
                  } else {
                    navigate('/pricing');
                  }
                } catch (err) {
                  console.error('[Portal Session Error]', err);
                  target.style.opacity = originalOpacity;
                  showNotification(t('portal_error') || 'Could not open subscription portal. Please try again.', 'error');
                }
              }}
            >
              <span className="settings-label-premium">
                {business?.polar_customer_id === 'pending' 
                  ? (t('activating') || 'Completing setup...') 
                  : (t('manage_subscription') || 'Manage Subscription')
                }
              </span>
              {business?.polar_customer_id !== 'pending' && <ChevronRight size={18} className="text-dim" />}
            </div>
          )}
        </div>

        <button className="btn-danger-premium" onClick={isDemo ? onDemoClick : signOut}>
          <LogOut size={18} />
          <span>{t('sign_out')}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="premium-dashboard">
      <header className="fixed-header" style={isDemo ? { top: '36px' } : {}}>
        <div className="nav-brand-group">
          <Logo size={24} />
          <span className="brand-text" style={{ fontSize: 18 }}>Wafiyo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="header-biz-name">{business.name}</div>
          <LanguageToggle />
        </div>
      </header>

      {isDemo && (
        <div className="demo-banner-fixed">
          <span className="demo-badge">Demo</span>
          <span className="demo-message-desktop">{t('demo_banner_text')}</span>
          <span className="demo-message-mobile">Demo Mode</span>
        </div>
      )}

      {isDemo && (
        <div className="demo-fab-mobile">
          <button
            onClick={signOut}
            className="btn-primary-gradient animate-pulse-custom"
            style={{
              padding: '14px 32px', borderRadius: '40px',
              fontSize: '16px', fontWeight: '800', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '10px', color: '#fff'
            }}
          >
            <Maximize size={20} />
            {t('cta_real_signup')}
          </button>
        </div>
      )}

      <div className="dashboard-content-scroll" style={isDemo ? { paddingTop: '120px' } : {}}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'clients' && renderClients()}
        {activeTab === 'settings' && renderSettings()}
      </div>

      {/* Install App Banner (mobile only) */}
      {!isDemo && <InstallBanner />}

      <nav className="bottom-nav-premium">
        <Link to="/dashboard" className="sidebar-brand-premium">
          <Logo size={28} />
          <span className="sidebar-brand-text">Wafiyo</span>
        </Link>
        <button
          onClick={() => setActiveTab('overview')}
          className={`nav-tab-premium ${activeTab === 'overview' ? 'active' : ''}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <LayoutDashboard size={24} />
          <span>{t('dashboard')}</span>
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          className={`nav-tab-premium ${activeTab === 'clients' ? 'active' : ''}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <Users size={24} />
          <span>{t('clients')}</span>
        </button>
        <Link to="/dashboard/card-design" className="nav-tab-premium">
          <Palette size={24} />
          <span>{t('design')}</span>
        </Link>
        <button
          onClick={() => setActiveTab('settings')}
          className={`nav-tab-premium ${activeTab === 'settings' ? 'active' : ''}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <Settings size={24} />
          <span>{t('settings')}</span>
        </button>

        {isDemo && (
          <div className="sidebar-cta-premium desktop-only">
            <span>{t('demo_sidebar_cta') || 'Ready to start your real loyalty program?'}</span>
            <button className="btn-sidebar-cta" onClick={signOut}>{t('cta_real_signup')}</button>
          </div>
        )}
      </nav>

      {/* Modals */}
      {showBizQR && <BusinessQRModal business={business} isDemo={isDemo} onClose={() => setShowBizQR(false)} />}
      {showAddModal && <AddClientModal businessId={business.id} onClose={() => setShowAddModal(false)} onSuccess={() => refreshDashboard(false)} />}
      {showScanner && <QRScanner onClose={() => { setShowScanner(false); refreshDashboard(false); }} />}
    </div>
  );
}
